import React, { ReactNode, useRef } from 'react';
import { COLORS, device, FONTS } from '../../constants';
import styled from 'styled-components';
import { motion, useInView } from 'motion/react';
import { ANIMATION_CONFIG } from '../../constants/animationConfig';

type SectionTitleProps = {
  title?: ReactNode;
  minHeight?: number;
  fontFamily?: string;
  color?: string;
  size?: number;
  weight?: number;
  lineHeight?: number;
  letterSpace?: React.CSSProperties['letterSpacing'];
  children?: ReactNode;
  isAnimated?: boolean;
  yOffset?: number;
  duration?: number;
  delay?: number;
};

export const Title = ({
  title,
  minHeight = 0,
  fontFamily = FONTS.TITLE,
  color = COLORS.SECTION_TITLE,
  size = 36,
  weight = 400,
  lineHeight = 54,
  letterSpace = 'normal',
  children,
  isAnimated = false,
  yOffset = ANIMATION_CONFIG.text.yOffset,
  duration = ANIMATION_CONFIG.text.duration,
  delay = ANIMATION_CONFIG.text.delay,
}: SectionTitleProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    amount: ANIMATION_CONFIG.text.amount,
  });

  return (
    <TitleText
      $minHeight={minHeight}
      $fontFamily={fontFamily}
      $color={color}
      $size={size}
      $weight={weight}
      $lineHeight={lineHeight}
      $letterSpace={letterSpace}
      ref={ref}
      {...(isAnimated
        ? {
            initial: { opacity: 0, y: yOffset },
            animate: isInView
              ? { opacity: 1, y: 0 }
              : { opacity: 0, y: yOffset },
            transition: {
              duration: duration,
              delay: delay,
              type: 'spring',
            },
          }
        : {})}
    >
      {title}
      {children}
    </TitleText>
  );
};

const TitleText = styled(motion.span)<{
  $minHeight?: number;
  $fontFamily?: string;
  $color?: string;
  $size?: number;
  $weight?: number;
  $lineHeight?: number;
  $letterSpace?: React.CSSProperties['letterSpacing'];
}>`
  min-height: ${props => `${props.$minHeight}px`};
  font-family: ${props => props.$fontFamily};
  color: ${props => props.$color};
  font-size: ${props => `${props.$size}px`};
  font-weight: ${props => props.$weight};
  line-height: ${props => `${props.$lineHeight}px`};
  letter-spacing: ${props => props.$letterSpace};
  white-space: pre-line;
  margin: 0;
  padding: 0;
  @media screen and (${device.md}) {
    font-size: 25px;
    line-height: 30px;
    min-height: 0;
  }
  @media screen and (${device.sm}) {
    font-size: 18px;
    line-height: 27px;
  }
`;
